import React from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, Calendar, Quote, CheckCircle2, User, Target, Heart, Camera, Briefcase, TrendingUp, Shield, BarChart3 } from 'lucide-react';

const OMne = () => {
  return (
    <div className="o-mne-page font-body text-brand-dark-blue overflow-x-hidden">
      {/* 1. HERO - VELKA OSOBNA FOTKA */}
      <section className="relative bg-brand-dark-blue text-white min-h-[90vh] flex items-center overflow-x-clip">
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'url(/symbol-3.svg)', backgroundSize: '400px', backgroundRepeat: 'repeat' }} />
        <div className="absolute top-1/2 right-0 w-[600px] h-[600px] bg-brand-orange/20 rounded-full blur-[150px] pointer-events-none" />

        <div className="container mx-auto px-4 sm:px-5 relative z-10 max-w-full min-w-0">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24 items-center min-w-0">
            {/* TEXT */}
            <div className="order-2 lg:order-1 max-w-xl min-w-0">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/10 text-brand-orange text-sm font-bold uppercase tracking-wide mb-8 backdrop-blur-sm">
                <User size={14} className="animate-pulse" />
                Môj príbeh
              </div>

              <h1 className="text-[1.75rem] min-[400px]:text-3xl sm:text-4xl lg:text-7xl font-heading font-bold mb-8 text-white leading-[1.12] sm:leading-[1.1] break-words">
                Systém porazí talent. <br /><span className="text-brand-orange text-xl min-[400px]:text-2xl sm:text-3xl lg:text-5xl">V športe aj v peniazoch.</span>
              </h1>

              <p className="text-base sm:text-xl lg:text-2xl text-gray-200 mb-10 leading-relaxed font-light">
                Dnes už nie som len sprostredkovateľ. Som <span className="text-white font-bold italic">architekt majetku</span>.
                <br /><span className="text-gray-400 text-lg mt-4 block">Pomáham ľuďom premeniť ich aktívny príjem na nekonečnú rentu.</span>
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <a href="#pribeh" className="inline-flex items-center justify-center bg-brand-orange hover:bg-red-600 text-white font-bold py-5 px-8 rounded-xl transition-all duration-300 shadow-xl hover:shadow-orange-500/20 text-lg group">
                  Spoznať môj príbeh
                </a>
              </div>
            </div>

            {/* PHOTO */}
            <div className="order-1 lg:order-2 relative">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl border border-white/10 transform -rotate-2 hover:rotate-0 transition-all duration-700">
                <div className="aspect-[3/4]">
                  <img src="/hero-peto.jpg" alt="Peto Sloboda" className="w-full h-full object-cover object-center" />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-brand-dark-blue/80 to-transparent opacity-40" />
              </div>
              <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-brand-orange rounded-full flex items-center justify-center text-white border-8 border-brand-dark-blue z-20 hidden md:flex rotate-12">
                <p className="text-xs font-bold text-center leading-tight tracking-widest uppercase">Poriadok<br />& Systém</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 2. STORYTELLING: HOCKEY -> BUSINESS -> FINANCE */}
      <section id="pribeh" className="py-16 sm:py-24 bg-white overflow-x-clip">
        <div className="container mx-auto px-4 sm:px-5 max-w-6xl min-w-0">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-16 lg:gap-32 items-start">

            {/* LEFT: TEXT */}
            <div className="space-y-16">
              <div className="relative">
                <div className="absolute left-0 sm:-left-8 top-0 text-6xl sm:text-8xl font-heading font-black text-gray-50 -z-10 pointer-events-none select-none" aria-hidden>01</div>
                <div className="w-12 h-12 bg-blue-50 rounded-full flex items-center justify-center text-brand-dark-blue mb-6">
                  <Target size={24} />
                </div>
                <h2 className="text-3xl lg:text-4xl font-heading font-bold mb-4 text-brand-dark-blue underline decoration-brand-orange/20 decoration-4 underline-offset-8">Disciplína z ľadu</h2>
                <div className="space-y-6 text-lg text-gray-600 leading-relaxed">
                  <p>
                    Do 24 rokov bol môj život nalinkovaný mantinelmi hokejového ihriska. Hral som profesionálne a naučil sa tam lekciu, ktorá mi neskôr zarobila viac než šport samotný: <strong>Výsledok nie je náhoda. Je to dôsledok systému.</strong>
                  </p>
                  <p>
                    Keď som vyzliekol dres, zistil som, že vo svete biznisu a financií väčšina ľudí žiadny systém nemá. Hrajú na náhodu. Skúšajú, čo „práve letí“, a dúfajú, že to vyjde.
                  </p>
                </div>
              </div>

              <div className="relative">
                <div className="absolute left-0 sm:-left-8 top-0 text-6xl sm:text-8xl font-heading font-black text-gray-50 -z-10 pointer-events-none select-none" aria-hidden>02</div>
                <div className="w-12 h-12 bg-orange-50 rounded-full flex items-center justify-center text-brand-orange mb-6">
                  <Briefcase size={24} />
                </div>
                <h2 className="text-3xl lg:text-4xl font-heading font-bold mb-4 text-brand-dark-blue underline decoration-brand-orange/20 decoration-4 underline-offset-8">Moja cesta</h2>
                <div className="space-y-6 text-lg text-gray-600 leading-relaxed">
                  <p>
                    Moja cesta viedla cez tvrdé podnikateľské začiatky, tiež investície do startupov (niektoré vyšli, iné boli drahou školou) až k vybudovaniu finančno-realitnej kancelárie.
                  </p>
                  <p>
                    Rýchlo som pochopil, že predávať finančné produkty je slepá ulička. Klientov nezaujíma názov fondu. <strong>Zaujíma ich, či ich majetok prežije infláciu, krízy aj ich vlastné chyby.</strong>
                  </p>
                </div>
              </div>

              <div className="relative">
                <div className="absolute left-0 sm:-left-8 top-0 text-6xl sm:text-8xl font-heading font-black text-gray-50 -z-10 pointer-events-none select-none" aria-hidden>03</div>
                <div className="w-12 h-12 bg-green-50 rounded-full flex items-center justify-center text-green-600 mb-6">
                  <Shield size={24} />
                </div>
                <h2 className="text-3xl lg:text-4xl font-heading font-bold mb-4 text-brand-dark-blue underline decoration-brand-orange/20 decoration-4 underline-offset-8">Dnes som architekt</h2>
                <div className="space-y-6 text-lg text-gray-600 leading-relaxed">
                  <p>
                    Pomáham podnikateľom, manažérom a ľuďom, ktorí chcú byť „normálne bohatí“, premeniť ich aktívny príjem na nekonečnú rentu.
                  </p>
                  <p>
                    <strong>Mojím cieľom nie je, aby ste bezhlavo investovali.</strong> Mojím cieľom je, aby ste mali systém, vďaka ktorému vás váš majetok uživí lepšie než vaša práca.
                  </p>
                </div>
              </div>
            </div>

            {/* RIGHT: DYNAMIC PHOTO GRID */}
            <div className="space-y-8 md:sticky md:top-24">
              <div className="grid grid-cols-2 gap-4">
                <img src="/pribeh_1z4.jpeg" className="rounded-3xl shadow-lg w-full h-[300px] object-cover hover:scale-[1.02] transition-transform" alt="Story 1" />
                <img src="/pribeh_2z4.jpeg" className="rounded-3xl shadow-lg w-full h-[400px] object-cover mt-12 hover:scale-[1.02] transition-transform" alt="Story 2" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <img src="/pribeh_3z4.jpeg" className="rounded-3xl shadow-lg w-full h-[400px] -mt-12 object-cover hover:scale-[1.02] transition-transform" alt="Story 3" />
                <img src="/pribeh_4z4.jpeg" className="rounded-3xl shadow-lg w-full h-[300px] object-cover hover:scale-[1.02] transition-transform" alt="Story 4" />
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* 3. MANIFESTO / VALUES */}
      <section className="py-24 bg-gray-50">
        <div className="container mx-auto px-4 max-w-5xl text-center">
          <Quote className="w-16 h-16 text-brand-orange/20 mx-auto mb-8" />
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold text-brand-dark-blue mb-12 sm:mb-16 leading-tight break-words px-1">
            Nehrám na dojmy.<br />
            <span className="text-brand-orange">Staviam na faktoch.</span>
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-left">
            {[
              {
                title: 'Čísla a pravidlá',
                text: 'Pracujem s presne definovanými pravidlami (ako napr. 200X, 300X). Intuícia je dobrá v hokeji, ale v majetku rozhodujú fakty.',
                icon: BarChart3
              },
              {
                title: 'Žiadne skratky',
                text: 'Budujem portfóliá, ktoré stoja na viacerých nohách – akcie, nehnuteľnosti, dlhopisy. Iba diverzifikácia prináša pokoj.',
                icon: Shield
              },
              {
                title: 'Zdielané know-how',
                text: 'Napsal som knihu Renta bez dátumu spotreby, aby mal každý prístup k informáciám, ktoré boli predtým len pre vyvolených.',
                icon: BookOpen
              }
            ].map((v, i) => (
              <div key={i} className="bg-white p-10 rounded-[2.5rem] shadow-sm border border-gray-100 hover:shadow-xl transition-shadow group">
                <div className="w-12 h-12 bg-gray-50 rounded-2xl flex items-center justify-center text-brand-orange mb-6 group-hover:bg-brand-orange group-hover:text-white transition-colors">
                  <v.icon size={24} />
                </div>
                <h3 className="font-bold text-2xl mb-4 text-brand-dark-blue group-hover:text-brand-orange transition-colors">{v.title}</h3>
                <p className="text-gray-600 text-lg leading-relaxed">{v.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 4. LIFE GALLERY */}
      <section className="py-16 sm:py-24 bg-white overflow-x-clip">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-between items-end mb-16 gap-8">
            <div className="max-w-2xl">
              <h2 className="text-4xl lg:text-5xl font-heading font-bold text-brand-dark-blue mb-6">Život za hranicou Excelu</h2>
              <p className="text-xl text-gray-600">Fotky sú môj spôsob, ako urobiť tento príbeh reálnym. Moja rodina, hory, priatelia a momenty, ktoré ma formujú.</p>
            </div>
            <div className="flex gap-4">
              <div className="w-14 h-14 rounded-full border border-gray-200 flex items-center justify-center text-gray-400">
                <Camera size={24} />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              '/pribeh_1z3.jpeg',
              '/pribeh_2z3.jpeg',
              '/pribeh_3z3.jpeg',
              '/pribeh_cta.jpeg',
              '/web-05.jpg',
              '/web-02.jpg',
              '/pribeh_s3.jpeg',
              '/hero-peto.jpg'
            ].map((src, i) => (
              <div key={i} className={`rounded-3xl overflow-hidden shadow-lg group ${i % 3 === 0 ? 'row-span-2 h-[600px]' : 'h-[285px]'}`}>
                <img src={src} className="w-full h-full object-cover transition-all duration-700 group-hover:scale-110" alt={`Life ${i}`} />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 5. FINAL CTA */}
      <section className="section relative bg-brand-dark-blue text-white overflow-x-clip py-20 sm:py-32">
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'url(/symbol-3.svg)', backgroundSize: '300px', backgroundRepeat: 'repeat' }} />
        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-brand-orange to-transparent" />

        <div className="container mx-auto px-4 relative z-10 text-center max-w-3xl">
          <h2 className="text-3xl sm:text-4xl lg:text-6xl font-heading font-bold mb-8 text-white leading-tight break-words">
            Sadnime si na kávu
          </h2>
          <p className="text-base sm:text-xl text-gray-300 mb-12 leading-relaxed">
            Ak hľadáte niekoho, kto bude k vašim peniazom pristupovať s rovnakou úctou a zodpovednosťou ako vy sami, som tu pre vás.
          </p>

          <div className="flex flex-col sm:flex-row gap-6 justify-center">
            <Link to="/konzultace" className="inline-flex items-center justify-center bg-brand-orange hover:bg-red-600 text-white font-bold py-5 px-10 rounded-2xl transition-all duration-300 shadow-lg text-lg transform hover:scale-105">
              <Calendar size={24} className="mr-3" />
              Dohodnúť stretnutie
            </Link>
            <a href="/kniha" className="inline-flex items-center justify-center bg-white/10 hover:bg-white/20 text-white font-bold py-5 px-10 rounded-2xl transition-all duration-300 border border-white/20 text-lg">
              Pozrieť si moju knihu
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default OMne;
